import { useTranslation } from "react-i18next";
import { useFormState } from "react-final-form";
import FormControl from "common/components/FormControl/FormControl";
import SectionTitle from "common/components/SectionTitle/SectionTitle";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
import { useEffect, useMemo } from "react";

interface PermissionToggleSectionProps {
  title: string;
  permissions: string[];
  basePath: string;
  form: any;
  editable: boolean;
}

function PermissionToggleSection({
  title,
  permissions,
  basePath,
  form,
  editable,
}: PermissionToggleSectionProps) {
  const { t } = useTranslation();
  const { values } = useFormState();

  // Séparer le basePath pour accéder aux données imbriquées
  const [baseObject, nestedKey] = basePath.split(".");

  useEffect(() => {
    if (values[baseObject]?.[nestedKey]) {
      const areAllChecked = permissions.every(
        (permission) => values[baseObject]?.[nestedKey]?.[permission] === true
      );
      form.change(`${basePath}.all`, areAllChecked);
    }
  }, [values, baseObject, nestedKey, permissions, form, basePath]);

  const handleToggleAll = (checked: boolean) => {
    permissions.forEach((permission) => {
      form.change(`${basePath}.${permission}`, checked);
    });
  };

  const permissionsHasReadKey = useMemo(() => {
    return permissions?.includes("read");
  }, [permissions]);

  const permissionsReadIsRead = useMemo(() => {
    return (
      permissionsHasReadKey && values[baseObject]?.[nestedKey]?.read === true
    );
  }, [baseObject, nestedKey, permissionsHasReadKey, values]);

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-row gap-3 items-center">
        <SectionTitle
          title={t(`administrationPermissions.${title}.${title}`)}
          className="mb-1"
        />
        <FormControl
          type="custom"
          name={`${basePath}.all`}
          editable={editable}
          onChange={handleToggleAll}
        >
          <ToggleSwitch isDisabled={!editable} />
        </FormControl>
      </div>
      {permissions.map((permission) =>
        permission !== "read" &&
        permissionsHasReadKey &&
        !permissionsReadIsRead ? null : (
          <FormControl
            key={`${basePath}.${permission}`}
            type="custom"
            editable={editable}
            name={`${basePath}.${permission}`}
          >
            <ToggleSwitch
              rightLabel={t(`administrationPermissions.${title}.${permission}`)}
            />
          </FormControl>
        )
      )}
    </div>
  );
}

export default PermissionToggleSection;
